import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageWrapper from '@/components/PageWrapper';
import Footer from '@/components/Footer';
import ErrorLine from '@/components/ErrorLine';
import OTPVerification from '@/components/OTPVerification';
import { useAuth } from '@/contexts/AuthWrapper';

const primary = 'var(--primary-base)';
const greyBg = 'var(--surface-surface_2)';
const greyText = 'var(--text-low_em)';
const errorCol = 'var(--error-color)';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { requestOTP, verifyOTP } = useAuth();
  const [email, setEmail] = useState('');
  const [focus, setFocus] = useState(false);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [showOTP, setShowOTP] = useState(false);

  const handleRequestOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErr('Invalid email');
      return;
    }
    setErr('');
    setLoading(true);
    try {
      await requestOTP(email, undefined, 'pwdreset');
      setShowOTP(true);
      console.log('OTP requested successfully for:', email);
      console.log('Check the browser console or server logs for the OTP code');
    } catch (e: unknown) {
      console.error('OTP request failed:', e);
      setErr((e as Error)?.message || 'Error requesting OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (otp: string) => {
    setLoading(true);
    try {
      const token = await verifyOTP(otp, 'pwdreset', email);
      navigate('/reset-password', {
        state: {
          actionToken: token,
          email: email,
        },
      });
    } catch (error: unknown) {
      console.error('OTP verification failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    try {
      await requestOTP(email, undefined, 'pwdreset');
    } catch (error: unknown) {
      console.error('Resend failed:', error);
      throw error;
    }
  };

  if (showOTP) {
    return (
      <div
        style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}
      >
        <main
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--surface-surface_0)',
          }}
        >
          <PageWrapper>
            <div
              style={{
                width: 480,
                padding: '48px 32px',
                borderRadius: 24,
                background: 'var(--surface-surface_0)',
                border: '1px solid var(--outline-base_em, #F4F4F6)',
                boxShadow:
                  '0px 1px 1px -0.5px var(--elevationshadow), 0px 3px 3px -1.5px var(--elevationshadow), 0px 24px 24px -12px var(--elevationshadow)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 32,
              }}
            >
              <h1
                style={{
                  fontFamily: 'Coolvetica, sans-serif',
                  fontSize: 64,
                  lineHeight: '48px',
                  color: primary,
                  margin: 0,
                  letterSpacing: '-0.64px',
                }}
              >
                swarmind
              </h1>

              <OTPVerification
                onVerify={handleVerifyOTP}
                onResend={handleResendOTP}
                isLoading={loading}
                error={err}
                action="pwdreset"
              />

              <button
                type="button"
                onClick={() => {
                  setShowOTP(false);
                  setErr('');
                }}
                style={{
                  border: 'none',
                  background: 'none',
                  color: primary,
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                ← Back to email input
              </button>
            </div>
          </PageWrapper>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div
      style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}
    >
      <main
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--surface-surface_0)',
        }}
      >
        <PageWrapper>
          <form
            onSubmit={handleRequestOTP}
            style={{
              width: 480,
              padding: '48px 32px',
              borderRadius: 24,
              background: 'var(--surface-surface_0)',
              border: '1px solid var(--outline-base_em, #F4F4F6)',
              boxShadow:
                '0px 1px 1px -0.5px var(--elevationshadow), 0px 3px 3px -1.5px var(--elevationshadow), 0px 24px 24px -12px var(--elevationshadow)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 32,
            }}
          >
            <h1
              style={{
                fontFamily: 'Coolvetica, sans-serif',
                fontSize: 64,
                lineHeight: '48px',
                color: primary,
                margin: 0,
                letterSpacing: '-0.64px',
              }}
            >
              swarmind
            </h1>

            <h2 style={{ fontSize: 24, margin: 0, fontWeight: 700 }}>
              Reset password
            </h2>
            <p
              style={{
                textAlign: 'center',
                fontSize: 14,
                color: greyText,
                marginTop: -12,
              }}
            >
              Enter your email address and we will send you a verification code
              to reset your password
            </p>

            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <label style={{ fontSize: 13, fontWeight: 500, color: greyText }}>
                Email
              </label>
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onFocus={() => setFocus(true)}
                onBlur={() => setFocus(false)}
                style={{
                  width: '100%',
                  height: 48,
                  borderRadius: 12,
                  background: focus ? 'var(--surface-surface_0)' : greyBg,
                  color: `var(--text-high_em)`,
                  border: err
                    ? `1px solid ${errorCol}`
                    : focus
                      ? `1px solid ${primary}`
                      : '1px solid transparent',
                  boxShadow: focus ? '0 0 0 3px rgba(94,100,255,0.2)' : 'none',
                  padding: '0 12px',
                  fontSize: 15,
                  boxSizing: 'border-box',
                  transition:
                    'border 0.15s, box-shadow 0.15s, background 0.15s',
                  outline: 'none',
                }}
              />
              <div
                style={{
                  width: 416,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  margin: '2px 0 0 0',
                }}
              >
                {err && <ErrorLine>{err}</ErrorLine>}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                height: 48,
                borderRadius: 12,
                background: loading ? greyBg : primary,
                color: loading ? greyText : 'var(--surface-surface_0)',
                fontWeight: 600,
                fontSize: 15,
                border: 'none',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                boxShadow:
                  '0 1px 1px -0.5px var(--elevationshadow), 0px 3px 3px -1.5px var(--elevationshadow), inset 0 3px 4px -3px rgba(255,255,255,0.56), inset 0 0 8px -2px rgba(255,255,255,0.48)',
              }}
            >
              {loading ? 'Sending…' : 'Continue'}
            </button>

            <button
              type="button"
              onClick={() => navigate('/login')}
              style={{
                border: 'none',
                background: 'none',
                color: primary,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              ← Back to Login
            </button>
          </form>
        </PageWrapper>
      </main>
      <Footer />
    </div>
  );
};

export default ForgotPasswordPage;
